/*
 * Copyright (c) Nexign, JSC, 2019-2024.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Maxim Fedorov
 * @author Aleksandr Perkhunov
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.33.0
 * @since 1.0.0
 */

import { FC, useCallback, useMemo } from 'react';

import { Banner as BannerUIKit } from '@nexign/scc-ui-kit';
import {
    IHideBannerPayload,
    IRegistrationClickAlternativeBannerPayload,
    IRegistrationShowAlternativeBannerPayload,
} from '@nexign/scc-ui-kit/lib/components/Banner/types';
import omit from 'lodash/omit';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import 'react-multi-carousel/lib/styles.css';
import { createStructuredSelector } from 'reselect';

import { makeSelectFeatureEnabled } from '@/main/pages/PageAuth/selectors/authSelectors';
import { IRootReducer } from '@/main/reducers/rootReducer';
import { ROUTE_CAMPAIGN_ID, RoutePath } from '@/main/routes/constants/routesConstants';

import { makeSelectTranslationCommon } from '../../internationalization/intSelectors';
import { ICommonTranslation } from '../../internationalization/types';
import { FeatureNameEnum } from '../../role/constants/guard/guardConstants';
import { registrationClickAlternativeBanner, registrationShowAlternativeBanner } from '../bannerActions';
import { INTERVAL_FOR_SLIDER, MAX_BANNERS_SHOW_COUNT } from '../bannerConstants';
import { EBannerType, IBannerItem } from '../bannerTypes';

export interface IBannerProps {
    banners: IBannerItem[];
    hideBanner: (payload: IHideBannerPayload) => void;
    hideAlternativeBanner: (payload: IHideBannerPayload) => void;
}

interface IDispatchProps {
    registrationShow: (payload: IRegistrationShowAlternativeBannerPayload) => void;
    registrationClick: (payload: IRegistrationClickAlternativeBannerPayload) => void;
}

type TBanner = IBannerProps & IDispatchProps & IConnectedProps;

export const Banner: FC<TBanner> = (props) => {
    const {
        banners,
        isCampaignsImprovementsFeature,
        translation,
        registrationClick,
        registrationShow,
        hideBanner,
        hideAlternativeBanner,
    } = props;
    const renderBanners = useMemo(() => {
        const bannerItems: IBannerItem[] = banners.map((banner) => {
            let urlOrClickHandler = banner.clickHandler;
            let buttonText = banner.buttonText;

            if (banner.type === EBannerType.CAMPAIGN) {
                urlOrClickHandler = RoutePath.campaignInfo.replace(ROUTE_CAMPAIGN_ID, banner.id.toString());
                buttonText = isCampaignsImprovementsFeature
                    ? translation.banner.moreDetails
                    : translation.banner.learnMore;
            }

            const bannerForUiKit = omit(banner, ['desktopImageURL', 'tabletImageURL', 'mobileImageURL']);

            return { ...bannerForUiKit, clickHandler: urlOrClickHandler, buttonText };
        });

        return bannerItems;
    }, [
        banners,
        isCampaignsImprovementsFeature,
        translation.banner.learnMore,
        translation.banner.moreDetails,
    ]);

    const maxBannersShowCount = isCampaignsImprovementsFeature ? renderBanners.length : MAX_BANNERS_SHOW_COUNT;

    const onRegistrationClickHandler = (payload: IRegistrationClickAlternativeBannerPayload) => {
        if (payload.type === EBannerType.ALTERNATE) {
            registrationClick(payload);
        } else if (payload.type === EBannerType.CAMPAIGN) {
            window.open(payload.clickHandler, '_blank');
        }
    };

    const onRegistrationShowHandler = (payload: IRegistrationShowAlternativeBannerPayload) => {
        if (payload.type === EBannerType.ALTERNATE) {
            registrationShow(payload);
        }
    };

    const onBannerHide = useCallback(
        (payload: IHideBannerPayload) => {
            if (payload.type === EBannerType.CAMPAIGN) {
                hideBanner({ id: payload.id });
            } else {
                hideAlternativeBanner({ id: payload.id });
            }
        },
        [hideBanner, hideAlternativeBanner],
    );

    return (
        <BannerUIKit
            banners={renderBanners}
            draggable={banners.length > 1}
            hideBanner={onBannerHide}
            intervalForSlide={INTERVAL_FOR_SLIDER}
            maxBannerShowCount={maxBannersShowCount}
            registrationClick={onRegistrationClickHandler}
            registrationShow={onRegistrationShowHandler}
            swipeable={banners.length > 1}
        />
    );
};

interface IConnectedProps {
    translation: ICommonTranslation;
    isCampaignsImprovementsFeature: boolean;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    translation: makeSelectTranslationCommon(),
    isCampaignsImprovementsFeature: makeSelectFeatureEnabled(FeatureNameEnum.CAMPAIGNS_IMPROVEMENT),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    registrationShow: (payload: IRegistrationShowAlternativeBannerPayload) =>
        dispatch(registrationShowAlternativeBanner(payload)),
    registrationClick: (payload: IRegistrationClickAlternativeBannerPayload) =>
        dispatch(registrationClickAlternativeBanner(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Banner);
